<template>
    <Head title="Home" />
    <BaseLayout gap="xl">
        <template #header>
            <!-- Schedule dialog kept for programmatic open (e.g. ?schedule_followup);
                 the top-right trigger buttons were removed. -->
            <ScheduleFollowupDialog ref="scheduleFollowupDialog" analytics-source="homepage" :coach-followup-events="coach_followup_events" />
        </template>
        <MobileAppBanner v-if="mobile_app_banner" :download-url="mobile_app_banner.download_url" :store-name="mobile_app_banner.store_name" />
        <div class="flex flex-col gap-2.5">
            <div class="flex flex-col gap-4">
                <!-- Org disclaimer -->
                <HomeDisclaimerMessage theme="info" :message="disclaimer_message ?? undefined" />

                <!-- User greeting -->
                <div class="flex gap-4 mt-10">
                    <h1 class="display-serif max-w-125 text-balance">{{ pageHeaderGreeting }}</h1>
                </div>
            </div>
            <!-- Start chat input area -->
            <HomeChatInput
                :document-upload-enabled="document_upload_enabled"
                :default-chat-redirect-url="default_chat_redirect_url"
                :placeholder="chatBarPlaceholder"
                :pii-patterns="pii_patterns"
                :better-voice-composer-enabled="better_voice_composer_enabled"
                :has-workiq-connected="has_workiq_connected" />
        </div>

        <!-- 360 Rater invite card (time-sensitive, shown before other sections) -->
        <RaterAssessmentHomeCard :invites="pending_rater_invites ?? []" />

        <!-- Wrapped invite card (seasonal) -->
        <HomeWrapped v-if="wrapped.can_start" :wrapped-url="wrapped.url" :wrapped-year="wrapped.year" :user-conversation-count="10" />

        <PerformanceManagementHomeCard
            :enabled="!!performance_review_moments?.moments?.length"
            :title="growthMomentsTitle"
            :homepage-card-title="performance_review_moments?.homepage_card_title ?? ''"
            :homepage-card-subtitle="performance_review_moments?.homepage_card_subtitle ?? ''"
            :moments="performance_review_moments?.moments ?? []" />

        <!-- The two WorkIQ sections, both only present for WorkIQ users with data. Insights lead
             because they are claims about now; the trajectory is the slower, reflective read and sits
             under it. -->
        <HomeMyWorkInsightsCard v-if="workiq_mywork_insights" :insights="workiq_mywork_insights" />

        <!-- WorkIQ longitudinal working patterns (PLT-1866); a second card over the same snapshot's
             metrics, not replaced by the insights one above -->
        <HomeMyWorkProgressCard v-if="mywork_progress" :progress="mywork_progress" />

        <HomeDiscoverBar
            :use-cases="discover_use_cases"
            :perspective-report-enabled="perspective_report_enabled"
            :perspective-report="perspective_report"
            :perspective-assessment-enabled="perspective_assessment_enabled"
            :perspective-card-dismissed="perspective_card_dismissed"
            :mywork-card="mywork_card"
            :profile-completion-card="profile_completion_card"
            :guided-setup-card="guided_setup_card"
            :collaboration-profile-enabled="collaboration_profile_enabled"
            :has-collaboration-profile="has_collaboration_profile"
            :homepage-mywork-insights-enabled="homepage_mywork_insights_enabled"
            :homepage-mywork-insights="homepage_mywork_insights"
            :work-iq-enabled="workiq_ff_enabled"
            :calendar-integration-enabled="calendar_integration.enabled"
            :has-workiq-connected="has_workiq_connected"
            @insight-hover="handleUseCaseHover"
            @insight-hover-end="handleUseCaseHoverEnd"
            @insight-clicked="startChatBarUseCase" />

        <!-- Tabs for remaining cards -->
        <BaseTabs v-model="activeTab" :tab-ids="visibleTabs" list-class="homepage-tabs-list">
            <!-- Calendar Tab -->
            <template #trigger-calendar>
                <div class="homepage-tab-trigger">
                    <Calendar class="size-4" />
                    <span>{{ calendarTabLabel }}</span>
                </div>
            </template>
            <template #content-calendar>
                <HomeCalendarTab
                    :initial-calendar-events="initial_calendar_events"
                    :calendar-coach-use-case="calendar_coach_use_case"
                    :calendar-insights-enabled="calendar_insights_homepage_enabled"
                    @coach-hover="handleUseCaseHover"
                    @coach-hover-end="handleUseCaseHoverEnd" />
            </template>

            <!-- Insights Tab -->
            <template #trigger-insights>
                <div class="homepage-tab-trigger">
                    <i class="bi bi-lightbulb text-base" aria-hidden="true"></i>
                    <span>Insights</span>
                </div>
            </template>
            <template #content-insights>
                <HomeInsightsTab :suggested-topics="suggested_topics" :insights="insights" @cta-click="handleCtaClick" />
            </template>

            <!-- Tracking Progress Tab -->
            <template #trigger-tracking-progress>
                <div class="homepage-tab-trigger">
                    <i class="bi bi-lightning-charge-fill text-base" aria-hidden="true"></i>
                    <span>{{ progressTabLabel }}</span>
                </div>
            </template>
            <template #content-tracking-progress>
                <HomeTrackingProgressTab
                    :action-items="action_items"
                    :coach-follow-up-events="coach_followup_events"
                    :default-chat-redirect-url="default_chat_redirect_url"
                    :user-feedback="user_feedback"
                    :wrapped="wrapped"
                    @cta-click="handleCtaClick" />
            </template>

            <!-- Improving at [Org] Tab-->
            <template #trigger-org-specific>
                <div class="homepage-tab-trigger" data-testid="org-specific-tab">
                    <img v-if="organizationDetails?.logo_square_url" :src="organizationDetails.logo_square_url" alt="" aria-hidden="true" class="max-w-5 max-h-5 object-contain" />
                    <i v-else class="bi bi-graph-up-arrow text-base" aria-hidden="true"></i>
                    <span>{{ orgTabLabel }}</span>
                </div>
            </template>
            <template #content-org-specific>
                <HomeImprovingAtOrgTab :prompts="organization_prompts" :suggested-topics="organization_suggested_topics" @cta-click="handleCtaClick" />
            </template>
        </BaseTabs>
    </BaseLayout>

    <CookieBanner />
    <PreferredLanguageNotice />

    <!-- Redirect error message, if any-->
    <BaseToast v-if="toast_error" theme="error">
        <template #description>
            <MarkdownText class="body-small" :text="toast_error" />
        </template>
    </BaseToast>

    <!-- Calendar Feature Carousel Dialog -->
    <BaseDialog
        v-if="shouldShowCalendarCarousel"
        v-model:open="isCalendarCarouselOpen"
        title="Connect your calendar"
        dialog-content-class="z-60 max-h-[85dvh] md:max-h-[90dvh]"
        overlay-class="z-60"
        @update:open="handleCalendarDialogOpenChange">
        <div class="flex justify-between items-center px-4 md:px-10 pt-7">
            <h3 class="flex items-center gap-2 title-body"><i class="bi bi-calendar" aria-hidden="true"></i> Connect your Calendar</h3>
            <button type="button" class="bi bi-x-lg icon-semibold" aria-label="Close" title="close feature carousel" @click="handleCalendarDialogOpenChange(false)"></button>
        </div>
        <FeatureCarousel @connect="handleConnectCalendar" />
    </BaseDialog>

    <!-- Calendar connection dialog. Lives at page level rather than inside the calendar tab so the
         store flag can be set from anywhere on the page (feature carousel, ?connect_calendar) — the
         tab's subtree is unmounted whenever another tab is active. -->
    <CalendarIntegrationSettings
        v-if="calendar_integration.enabled"
        v-model:open="useCalendarStore.showConnectCalendarDialog"
        @close="useCalendarStore.setShowConnectCalendarDialog(false)" />

    <!-- Coaching Context Carousel Dialog -->
    <BaseDialog
        v-if="isCoachingContextCarouselOpen"
        v-model:open="isCoachingContextCarouselOpen"
        title="Get more from Nadia"
        dialog-content-class="z-60 md:max-w-4xl max-h-[85dvh] md:max-h-[90dvh] bg-[#F7F5F2]"
        overlay-class="z-60"
        @update:open="handleCoachingContextCarouselOpenChange">
        <CoachingContextCarousel
            :first-name="userDetails?.first_name ?? undefined"
            location="home"
            :connections-enabled="coaching_context_carousel?.connections_enabled ?? true"
            :collab-profile-enabled="coaching_context_carousel?.collab_profile_enabled ?? true"
            :calendar-enabled="coaching_context_carousel?.calendar_enabled ?? true"
            :cta-loading="isCoachingContextCarouselCtaLoading"
            @dismiss="handleCoachingContextCarouselOpenChange(false)"
            @cta-click="handleCoachingContextCarouselCtaClick" />
    </BaseDialog>
</template>

<script lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, usePage } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { GlobalProperties, OrganizationDetails, PerspectiveReport, UserDetails } from "~vue/appTypes";
import CoachingContextCarousel from "~vue/ChatHome/CoachingContextCarousel.vue";
import HomeCalendarTab from "~vue/ChatHome/HomeCalendarTab.vue";
import HomeChatInput from "~vue/ChatHome/HomeChatInput.vue";
import HomeDisclaimerMessage from "~vue/ChatHome/HomeDisclaimerMessage.vue";
import HomeDiscoverBar from "~vue/ChatHome/HomeDiscoverBar.vue";
import HomeImprovingAtOrgTab from "~vue/ChatHome/HomeImprovingAtOrgTab.vue";
import HomeInsightsTab from "~vue/ChatHome/HomeInsightsTab.vue";
import HomeMyWorkInsightsCard from "~vue/ChatHome/HomeMyWorkInsightsCard.vue";
import HomeMyWorkProgressCard from "~vue/ChatHome/HomeMyWorkProgressCard.vue";
import HomeTrackingProgressTab from "~vue/ChatHome/HomeTrackingProgressTab.vue";
import HomeWrapped from "~vue/ChatHome/HomeWrapped.vue";
import MobileAppBanner from "~vue/ChatHome/MobileAppBanner.vue";
import PerformanceManagementHomeCard from "~vue/ChatHome/PerformanceManagementHomeCard.vue";
import RaterAssessmentHomeCard from "~vue/ChatHome/RaterAssessmentHomeCard.vue";
import { isRolePlayUseCase } from "~vue/ChatUseCaseEntrance/utils";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import FeatureCarousel from "~vue/components/CalendarIntegration/FeatureCarousel.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import PreferredLanguageNotice from "~vue/components/PreferredLanguageNotice.vue";
import ScheduleFollowupDialog from "~vue/components/ScheduleFollowupDialog.vue";
import { useUIInteractions } from "~vue/composables/useUIInteractions";
import Calendar from "~vue/icons/Calendar.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import type { SetupHomeCard } from "~vue/ProfileSetup/types";
import { useCalendarStore } from "~vue/stores/calendarStore";
import type { CalendarIntegrationViewEvent } from "~vue/types/calendar_integration";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import type {
    ActionItem,
    CalendarIntegration,
    ChatEntranceUseCasesResponse,
    CoachFollowupEvent,
    HomeCard,
    HomepageCustomization,
    HomepageInsight,
    HomepageMyWorkInsight,
    MyWorkCardData,
    MyWorkInsightsCardData,
    MyWorkProgressData,
    OrganizationPrompt,
    OrganizationSuggestedTopic,
    PendingRaterInvite,
    ProfileCompletionSummary,
    SuggestedTopic,
    UserFeedback,
    Wrapped,
} from "~vue/types/chat_home";
import type { PerformanceReviewMoments } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants.js";
import { CARD_TYPE } from "~vue/utils/homeCards";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { DateTime } from "luxon";
import { computed, inject, onMounted, onUnmounted, provide, type Ref, ref, useTemplateRef, watch } from "vue";

export interface ChatHomeProps {
    coach_followup_events: CoachFollowupEvent[];
    default_chat_redirect_url: string;
    event_context: { window_id: string };
    disclaimer_message: string | null;
    is_first_time_user: boolean;
    action_items: ActionItem[];
    suggested_topics: SuggestedTopic[];
    organization_prompts: OrganizationPrompt[];
    organization_suggested_topics: OrganizationSuggestedTopic[];
    insights?: HomepageInsight[];
    role_play_setup_url: string;
    user_feedback: UserFeedback;
    calendar_insights_homepage_enabled: boolean;
    document_upload_enabled: boolean;
    calendar_integration: CalendarIntegration;
    initial_calendar_events: CalendarIntegrationViewEvent[] | null;
    calendar_coach_use_case: ChatEntranceUseCaseData | null;
    toast_error: string | null;
    is_feature_carousel_dismissed: boolean;
    coaching_context_carousel?: {
        should_show: boolean;
        connections_enabled: boolean;
        collab_profile_enabled: boolean;
        calendar_enabled: boolean;
    };
    discover_use_cases: ChatEntranceUseCasesResponse;
    wrapped: Wrapped;
    perspective_report_enabled: boolean;
    perspective_report: PerspectiveReport | null;
    perspective_assessment_enabled: boolean;
    perspective_card_dismissed: boolean;
    collaboration_profile_enabled: boolean;
    has_collaboration_profile: boolean;
    performance_review_moments: PerformanceReviewMoments;
    pii_patterns?: PatternEntry[] | null;
    homepage_insights_tab_enabled: boolean;
    homepage_customization?: HomepageCustomization | null;
    better_voice_composer_enabled?: boolean;
    pending_rater_invites?: PendingRaterInvite[];
    mywork_card?: MyWorkCardData | null;
    mywork_progress?: MyWorkProgressData | null;
    workiq_mywork_insights?: MyWorkInsightsCardData | null;
    profile_completion_card?: ProfileCompletionSummary | null;
    guided_setup_card?: SetupHomeCard | null;
    hide_nadia_name?: boolean;
    homepage_mywork_insights_enabled?: boolean;
    homepage_mywork_insights?: HomepageMyWorkInsight[];
    workiq_ff_enabled?: boolean;
    has_workiq_connected?: boolean;
    mobile_app_banner?: {
        download_url: string;
        store_name: string;
    } | null;
}

const _props = withDefaults(defineProps<ChatHomeProps>(), {
    action_items: () => [],
    calendar_coach_use_case: null,
    calendar_insights_homepage_enabled: false,
    calendar_integration: () => ({ enabled: false, timezones: [], calendar_provider: null, dismissed: false, private_toggle_enabled: false, allow_private_events: null }),
    coach_followup_events: () => [],
    default_chat_redirect_url: "/",
    disclaimer_message: null,
    discover_use_cases: () => ({ enabled: false, cases: [] }),
    document_upload_enabled: true,
    event_context: () => ({ window_id: "chat_home" }),
    initial_calendar_events: null,
    insights: () => [],
    is_feature_carousel_dismissed: false,
    coaching_context_carousel: undefined,
    is_first_time_user: true,
    organization_prompts: () => [],
    organization_suggested_topics: () => [],
    performance_review_moments: () => ({}),
    toast_error: null,
    user_feedback: () => ({ enabled: false, feedback_response_count_min: null }),
    perspective_report_enabled: false,
    perspective_report: null,
    perspective_assessment_enabled: false,
    perspective_card_dismissed: false,
    collaboration_profile_enabled: false,
    has_collaboration_profile: false,
    wrapped: () => ({ can_access: false, can_start: false, show_completed: false, url: "", year: 0, action_items: [] }),
    pii_patterns: null,
    homepage_insights_tab_enabled: false,
    better_voice_composer_enabled: false,
    pending_rater_invites: () => [],
    hide_nadia_name: false,
    suggested_topics: () => [],
    homepage_customization: null,
    mywork_card: null,
    mywork_progress: null,
    workiq_mywork_insights: null,
    profile_completion_card: null,
    guided_setup_card: null,
    homepage_mywork_insights_enabled: false,
    homepage_mywork_insights: () => [],
    workiq_ff_enabled: false,
    has_workiq_connected: false,
    mobile_app_banner: null,
});

const {
    action_items,
    calendar_coach_use_case,
    calendar_insights_homepage_enabled,
    calendar_integration,
    coach_followup_events,
    default_chat_redirect_url,
    disclaimer_message,
    discover_use_cases,
    document_upload_enabled,
    event_context,
    initial_calendar_events,
    is_feature_carousel_dismissed,
    is_first_time_user,
    organization_prompts,
    organization_suggested_topics,
    performance_review_moments,
    toast_error,
    user_feedback,
    perspective_report_enabled,
    perspective_report,
    perspective_assessment_enabled,
    collaboration_profile_enabled,
    has_collaboration_profile,
    perspective_card_dismissed,
    wrapped,
    pii_patterns,
    homepage_insights_tab_enabled,
    better_voice_composer_enabled,
    pending_rater_invites,
    hide_nadia_name,
    suggested_topics,
    insights,
    mywork_card,
    mywork_progress,
    workiq_mywork_insights,
    coaching_context_carousel,
    profile_completion_card,
    guided_setup_card,
    homepage_mywork_insights_enabled,
    homepage_mywork_insights,
    workiq_ff_enabled,
    has_workiq_connected,
    mobile_app_banner,
} = _props;

provide("calendarIntegration", calendar_integration);

const globalProperties = inject<GlobalProperties>("globalProperties");
const userDetails = inject<Ref<UserDetails>>("user_details");
const organizationDetails = inject<Ref<OrganizationDetails>>("organization_details");

const scheduleFollowupDialog = useTemplateRef("scheduleFollowupDialog");

const pageHeaderGreeting = computed(() => {
    const userFirstName = userDetails?.value?.first_name;
    if (is_first_time_user) {
        if (hide_nadia_name) {
            return `Hi ${userFirstName ?? "there"}, welcome to your AI Coach`;
        }
        return `Hi ${userFirstName ?? "there"}, I'm Nadia, your AI Coach`;
    }
    return `Welcome back${userFirstName ? `, ${userFirstName}` : ""}`;
});

const perfReviewCustomCopy = computed(() => organizationDetails?.value?.perf_review_custom_copy ?? {});
const growthMomentsTitle = computed(() => perfReviewCustomCopy.value?.page_tab_label || "Performance Management");
const homepageCustomization = computed(() => _props.homepage_customization);
const orgTabLabel = computed(() => {
    const prefix = homepageCustomization.value?.org_tab_prefix?.trim() ?? "Succeeding at";
    const orgName = organizationDetails?.value?.name?.trim() ?? "Your Organization";
    return `${prefix} ${orgName}`;
});
const progressTabLabel = computed(() => homepageCustomization.value?.progress_tab_name?.trim() || "Tracking Progress");
const calendarTabLabel = computed(() => homepageCustomization.value?.calendar_tab_name?.trim() || "Calendar");

const startChatBarUseCase = (use_case: ChatEntranceUseCaseData) => {
    if (isRolePlayUseCase(use_case)) {
        openUrl(_props.role_play_setup_url);
        return;
    }
    openUrl(default_chat_redirect_url, {
        queryParams: {
            chat_source: use_case.chat_source,
            script: use_case.call_code,
        },
    });
};

const handleUseCaseHover = (useCase: ChatEntranceUseCaseData) => {
    hoveredButtonDescription.value = useCase.description ?? undefined;
};

const handleUseCaseHoverEnd = () => {
    hoveredButtonDescription.value = undefined;
};

enum TabId {
    Calendar = "calendar",
    TrackingProgress = "tracking-progress",
    OrgSpecific = "org-specific",
    Insights = "insights",
}

const isOrgSpecificTabVisible = computed(() => !!(organization_prompts.length || organization_suggested_topics.length));
const isInsightsTabVisible = computed(() => homepage_insights_tab_enabled && !!(suggested_topics.length || insights.length));
const isCalendarTabVisible = computed(() => !!calendar_integration.enabled);
// This logic should match the visibleCardTypes computed in HomeTrackingProgressTab
const isTrackingProgressTabVisible = computed(
    () =>
        !!(
            action_items.length ||
            wrapped.show_completed ||
            wrapped.action_items.length ||
            user_feedback.enabled ||
            coach_followup_events.some((event) => DateTime.now().hasSame(convertToDateTime(event.event_at_confirmed), "day"))
        ),
);

const TAB_ORDER_KEY: Partial<Record<TabId, keyof NonNullable<NonNullable<typeof homepageCustomization.value>["tabs_order"]>>> = {
    [TabId.Calendar]: "calendar",
    [TabId.TrackingProgress]: "tracking_progress",
    [TabId.OrgSpecific]: "org_specific",
};

const visibleTabs = computed((): TabId[] => {
    const visible = [
        isCalendarTabVisible.value && TabId.Calendar,
        isOrgSpecificTabVisible.value && TabId.OrgSpecific,
        isInsightsTabVisible.value && TabId.Insights,
        isTrackingProgressTabVisible.value && TabId.TrackingProgress,
    ].filter((t): t is TabId => !!t);

    const tabsOrder = homepageCustomization.value?.tabs_order;
    if (!tabsOrder) {
        return visible;
    }

    const orderOf = (t: TabId) => {
        const key = TAB_ORDER_KEY[t];
        return (key && tabsOrder[key]) ?? Infinity;
    };
    return visible.toSorted((a, b) => orderOf(a) - orderOf(b));
});
const activeTab = ref<TabId>(visibleTabs.value[0]);

watch(activeTab, (tab) => {
    if (tab) {
        logUserInteraction(AmplitudeEventNames.HOMEPAGE_TAB_CLICKED, { tab });
    }
});

const handleCtaClick = (card: HomeCard) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, {
        card_type: card.type,
        title: card.title,
        external_name: card.external_name,
        source: card.source,
        location: HOMEPAGE_CARD_LOCATION.HOME,
    });

    const cardUrl = card.url;
    if (!cardUrl) {
        return;
    }

    switch (card.type) {
        case CARD_TYPE.FOLLOW_UP:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.HOME_FOLLOW_UP } });
            break;
        case CARD_TYPE.ORGANIZATION:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.TRENDING_ORG_PROMPT } });
            break;
        case CARD_TYPE.SUGGESTED_TOPICS:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.HOME_SUGGESTED_TOPICS } });
            break;
        case CARD_TYPE.INSIGHTS:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.HOME_INSIGHTS } });
            break;
        default:
    }
};

const hoveredButtonDescription = ref<string | undefined>(undefined);
const chatBarPlaceholder = computed(() => hoveredButtonDescription.value ?? "What's on your mind?");

// Coaching context carousel — takes priority over other home screen popups
const isCoachingContextCarouselOpen = ref(false);
const isCoachingContextCarouselCtaLoading = ref(false);
const { createInteraction } = useUIInteractions();

const calendarFeatureCarouselEnabled = computed(() => {
    return calendar_integration.enabled && !calendar_integration.dismissed && !is_feature_carousel_dismissed && !userDetails?.value?.has_calendar_integration; // dont show if already integrated calendar
});

const isMobileApp = usePage().props?.is_mobile_app;
// Suppress calendar carousel when coaching context carousel should fire (it takes priority)
const shouldShowCalendarCarousel = computed(
    () => calendarFeatureCarouselEnabled.value && !isMobileApp && !coaching_context_carousel?.should_show && !isCoachingContextCarouselOpen.value,
);

const isCalendarCarouselOpen = ref(shouldShowCalendarCarousel.value);

// Deep link that lands on the homepage with the calendar connection dialog already open, so a
// campaign email or Slack nudge can drop the user straight into the flow (ENG-1903).
const CONNECT_CALENDAR_PARAM = "connect_calendar";
// Optional attribution on that link, naming which campaign or nudge sent the user.
const DEEPLINK_SOURCE_PARAM = "source";
// The link is public, so its ?source is whatever the caller typed. Cap it before it becomes an
// Amplitude property value, since Amplitude keeps every distinct value it is ever sent.
const DEEPLINK_SOURCE_MAX_LENGTH = 64;

const openConnectCalendarDialog = (source: string, deeplinkSource?: string) => {
    isCalendarCarouselOpen.value = false;
    useCalendarStore.setShowConnectCalendarDialog(true);
    logUserInteraction(AmplitudeEventNames.CALENDAR_INTEGRATION_STARTED, {
        source,
        ...(deeplinkSource ? { deeplink_source: deeplinkSource } : {}),
    });
};

const handleConnectCalendar = () => openConnectCalendarDialog("carousel");

const updateDismissPayload = ref<{ dismissed_feature_carousel_at: string } | null>(null);
const { execute: updateDismissFeatureCarousel } = useFetch("api/accounts/user", { immediate: false }).put(updateDismissPayload).json();

const handleCalendarDialogOpenChange = async (isOpen: boolean) => {
    if (!isOpen) {
        isCalendarCarouselOpen.value = false;
        if (!is_feature_carousel_dismissed) {
            updateDismissPayload.value = { dismissed_feature_carousel_at: DateTime.now().toISO() };
            await updateDismissFeatureCarousel();
            updateDismissPayload.value = null;
        }
    }
};

const handleCoachingContextCarouselOpenChange = async (isOpen: boolean) => {
    isCoachingContextCarouselOpen.value = isOpen;
    if (!isOpen) {
        logUserInteraction(AmplitudeEventNames.COACHING_CONTEXT_CAROUSEL_DISMISSED, { location: "home" });
        await createInteraction("coaching_context_carousel_dismissed");
    }
};

async function handleCoachingContextCarouselCtaClick() {
    isCoachingContextCarouselCtaLoading.value = true;
    try {
        await createInteraction("coaching_context_carousel_cta_clicked");
    } finally {
        isCoachingContextCarouselCtaLoading.value = false;
        isCoachingContextCarouselOpen.value = false;
    }
    openUrl("/accounts/user-profile");
}

onMounted(() => {
    globalProperties?.$setEventContext(event_context);
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("schedule_followup")) {
        scheduleFollowupDialog.value?.open();
    }
    if (params.get(CONNECT_CALENDAR_PARAM) && calendar_integration.enabled) {
        openConnectCalendarDialog("deeplink", params.get(DEEPLINK_SOURCE_PARAM)?.trim().slice(0, DEEPLINK_SOURCE_MAX_LENGTH) || undefined);
    }
    if (coaching_context_carousel?.should_show) {
        isCalendarCarouselOpen.value = false;
        isCoachingContextCarouselOpen.value = true;
    }
});

onUnmounted(() => {
    globalProperties?.$setEventContext({});
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

:deep(.homepage-tabs-list) {
    @apply lg:sticky lg:top-0 pt-4 mb-4 z-10 -mx-6 md:mx-0 px-6 md:px-0 bg-valence-page;
}

.homepage-tab-trigger {
    @apply flex items-center gap-2 whitespace-nowrap;
}
</style>
